import React, { useState, useRef } from "react";
import api, { API, extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Eye, AlertTriangle, CheckCircle2 } from "lucide-react";
import { formatArabicDateTime } from "@/pages/admin/utils/format";



const BookUploader = ({ bookPdf, onChanged }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("الملف يجب أن يكون PDF");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/book/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      toast.success("تم رفع الكتاب بنجاح");
      onChanged && onChanged();
    } catch {
      toast.error("تعذّر رفع الملف");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const previewCurrent = async () => {
    setPreviewing(true);
    try {
      // Direct download via <a download> — avoids ERR_BLOCKED_BY_CLIENT that
      // hits when ad-blockers refuse blob: window.open() popups.
      const res = await api.get("/admin/book/preview", { responseType: "blob", timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = (bookPdf && bookPdf.filename) || "thabiti-awtadak.pdf";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // give the browser a moment to start the download before we revoke
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success("بدأ تحميل الملف");
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر تحميل الملف"));
    } finally {
      setPreviewing(false);
    }
  };

  const exists = !!(bookPdf && bookPdf.exists);
  const sizeKb = exists ? Math.round((bookPdf.size_bytes || 0) / 1024) : 0;
  const lastModified = exists && bookPdf.last_modified ? formatArabicDateTime(bookPdf.last_modified) : "";

  return (
    <div className="card-warm p-6">
      <h3 className="font-heading text-xl text-[#222222]">إدارة ملف الكتاب</h3>
      <p className="text-[#5E5C58] text-sm mt-1">
        رفع نسخة جديدة من «ثبتي أوتادك» (PDF). سيستلمها كل من يدفع لاحقًا.
      </p>

      {/* Status panel */}
      {exists ? (
        <div
          className="mt-4 rounded-2xl border border-[#A7F3D0] bg-[#ECFDF5] p-4 text-sm"
          data-testid="admin-book-status-uploaded"
        >
          <div className="flex items-start gap-2 text-[#047857] font-semibold">
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
            <span>✅ يوجد ملف كتاب مرفوع حاليًا جاهز للتسليم.</span>
          </div>
          <div className="mt-2 ps-7 text-[#222222] leading-7">
            <div>
              <span className="text-[#5E5C58]">اسم الملف: </span>
              <span className="font-mono" dir="ltr">{bookPdf.filename}</span>
              <span className="text-[#5E5C58] ms-2">({sizeKb.toLocaleString("ar-SA")} KB)</span>
            </div>
            {lastModified && (
              <div>
                <span className="text-[#5E5C58]">آخر تحديث: </span>
                <span>{lastModified}</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          className="mt-4 rounded-2xl border border-[#FED7AA] bg-[#FFF7ED] p-4 text-sm flex items-start gap-2 text-[#9A3412]"
          data-testid="admin-book-status-missing"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <span>
            ⚠️ لم يتم رفع أي ملف للكتاب حتى الآن. يرجى رفع الملف ليتمكن العملاء من تحميله.
          </span>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        onChange={upload}
        className="hidden"
        data-testid="admin-book-upload-input"
      />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
          data-testid="admin-book-upload-btn"
        >
          <Upload className="w-4 h-4 ms-2" />
          {uploading ? "جاري الرفع..." : "رفع ملف PDF جديد"}
        </Button>
        {exists && (
          <Button
            type="button"
            variant="outline"
            onClick={previewCurrent}
            disabled={previewing}
            className="rounded-full border-[#0A4D3C] text-[#0A4D3C] hover:bg-[#F6F4EE]"
            data-testid="admin-book-preview-btn"
          >
            <Eye className="w-4 h-4 ms-2" />
            {previewing ? "جاري التحميل..." : "تحميل / معاينة الملف الحالي"}
          </Button>
        )}
      </div>
    </div>
  );
};

export { BookUploader };
